import React, { useState, useEffect } from "react";
import axios from "axios";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import "./exchangefees.css";

const ExchangeFees = () => {
  const [activeMainTab, setActiveMainTab] = useState("exchange");
  const [activeExchangeTab, setActiveExchangeTab] = useState("trading-fee");
  const [activePaymentsTab, setActivePaymentsTab] = useState("txn-fees");
  const [activeICOTab, setActiveICOTab] = useState("txn-fees");

  const [tradingCurrency, setTradingCurrency] = useState("");
  const [isFutures, setIsFutures] = useState(1);
  const [isOptions, setIsOptions] = useState(1);

  // Data states
  const [tradingFees, setTradingFees] = useState([]);
  const [dailyLimits, setDailyLimits] = useState([]);
  const [depositWithdrawalFees, setDepositWithdrawalFees] = useState([]);
  const [futuresData, setFuturesData] = useState([]);
  const [optionsData, setOptionsData] = useState([]);
  const [fiatLimits, setFiatLimits] = useState([]);
  const [collateralDeposit, setCollateralDeposit] = useState([]);
  const [loanableAssets, setLoanableAssets] = useState({});
  const [earnAssets, setEarnAssets] = useState([]);
  const [p2pFees, setP2pFees] = useState([]);
  const [paymentsFees, setPaymentsFees] = useState([]);
  const [paymentsPlans, setPaymentsPlans] = useState([]);
  const [icoFees, setIcoFees] = useState([]);
  const [icoPlans, setIcoPlans] = useState([]);
  const [nftFees, setNftFees] = useState([]);
  const [brokerMarket, setBrokerMarket] = useState({});
  const [loading, setLoading] = useState(true);
  const [paymentsTxnFees, setPaymentsTxnFees] = useState([]);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([
      fetchBaseCurrency(),
      fetchTradingFees(),
      fetchDailyLimits(),
      fetchDepositWithdrawalFees(),
      fetchFuturesData(),
      fetchOptionsData(),
      fetchCollateralDeposit(),
      fetchLoanableAssets(),
      fetchEarnAssets(),
      fetchP2PFees(),
      fetchPaymentsFees(),
      fetchICOFees(),
      fetchNFTFees(),
      fetchBrokerMarket(),
    ]);
    setLoading(false);
  };

  const fetchPaymentsFees = async () => {
    try {
      const brokerId = localStorage.getItem("BROKERID");

      // New API for transaction fees
      const txnResponse = await axios.get(
        `https://service.hashcashconsultants.com/billbitcoins-v2/payment/getTxnChargeDetailsByBroker/${brokerId}`
      );
      // Deduplicate by currencyCode (multiple merchantIds per currency)
      const unique = {};
      txnResponse.data.results.forEach((item) => {
        if (!unique[item.currencyCode]) {
          unique[item.currencyCode] = item;
        }
      });
      setPaymentsTxnFees(Object.values(unique));

      // Old API still used for plans
      const plansResponse = await axios.get(
        "https://service.hashcashconsultants.com/billbitcoins-v2/merchant/platform_fees"
      );
      setPaymentsPlans(plansResponse.data.plans);
    } catch (error) {
      console.error("Error fetching payments fees:", error);
    }
  };

  const fetchBrokerMarket = async () => {
    try {
      const response = await axios.post(
        "https://accounts.paybito.com/api/user/cryptoMarketByBroker",
        { brokerId: localStorage.getItem("BROKERID") }
      );
      if (response.data && response.data.brokerCryptoMarket) {
        setBrokerMarket(response.data.brokerCryptoMarket);
      }
    } catch (error) {
      console.error("Error fetching broker market config:", error);
    }
  };

  const fetchBaseCurrency = async () => {
    try {
      const response = await axios.get(
        `https://accounts.paybito.com/api/userTrade/spotTradingFees/${localStorage.getItem("BROKERID")}`
      );
      setTradingCurrency(response.data.tradingCurrency);
      setIsFutures(response.data.futures);
      setIsOptions(response.data.options);
    } catch (error) {
      console.error("Error fetching base currency:", error);
    }
  };

  const fetchTradingFees = async () => {
    try {
      const response = await axios.get(
        `https://accounts.paybito.com/api/userTrade/spotTradingFees/${localStorage.getItem("BROKERID")}`
      );
      setTradingFees(response.data.value);
    } catch (error) {
      console.error("Error fetching trading fees:", error);
    }
  };

  const fetchDailyLimits = async () => {
    try {
      const response = await axios.get(
        "https://accounts.paybito.com/api/user/tierWiseSettingsDetails/" +
        localStorage.getItem("BROKERID")
      );
      const filteredData = response.data.response.filter(
        (item) => !["AED", "USD", "INR", "SGD", "EUR"].includes(item.currency)
      );
      setDailyLimits(filteredData);
    } catch (error) {
      console.error("Error fetching daily limits:", error);
    }
  };

  const fetchDepositWithdrawalFees = async () => {
    try {
      const miningResponse = await axios.get(
        "https://accounts.paybito.com/api/transaction/miningFeesDetails/" +
        localStorage.getItem("BROKERID")
      );
      const tierResponse = await axios.get(
        "https://accounts.paybito.com/api/user/tierWiseSettingsDetails/" +
        localStorage.getItem("BROKERID")
      );

      setDepositWithdrawalFees(miningResponse.data.response);

      const fiatData = tierResponse.data.response.filter((item) =>
        ["AED", "USD", "INR", "SGD", "EUR"].includes(item.currency)
      );
      setFiatLimits(fiatData);
    } catch (error) {
      console.error("Error fetching deposit/withdrawal fees:", error);
    }
  };

  const fetchFuturesData = async () => {
    try {
      const response = await axios.get(
        "https://accounts.paybito.com/api/fMargin/fundingHomeView"
      );
      setFuturesData(response.data.P_FUNDING_CURRENCY);
    } catch (error) {
      console.error("Error fetching futures data:", error);
    }
  };

  const fetchOptionsData = async () => {
    try {
      const response = await axios.get(
        "https://accounts.paybito.com/api/optionsMargin/fundingHomeView"
      );
      setOptionsData(response.data.P_FUNDING_CURRENCY);
    } catch (error) {
      console.error("Error fetching options data:", error);
    }
  };

  const fetchCollateralDeposit = async () => {
    try {
      const response = await axios.get(
        "https://accounts.paybito.com/api/home/tradingVolumeWiseDeposit"
      );
      setCollateralDeposit(response.data.value);
    } catch (error) {
      console.error("Error fetching collateral deposit:", error);
    }
  };

  const fetchLoanableAssets = async () => {
    try {
      const response = await axios.get(
        "https://accounts.paybito.com/api/crypto/banking/loan/getLoanInterestRate/PAYB18022021121103"
      );
      if (response.data.loanMarketDetails) {
        const groupedData = {};
        response.data.loanMarketDetails.forEach((item) => {
          if (!groupedData[item.loanAsset]) {
            groupedData[item.loanAsset] = {};
          }
          groupedData[item.loanAsset][item.loanDurationId] = item;
        });
        setLoanableAssets(groupedData);
      }
    } catch (error) {
      console.error("Error fetching loanable assets:", error);
    }
  };

  const fetchEarnAssets = async () => {
    try {
      const brokerId = localStorage.getItem("BROKERID");
      const response = await axios.get(
        `https://accounts.paybito.com/api/crypto/banking/earn/getAssetWiseEarnInterests?brokerId=${brokerId}`
      );
      if (response.data && response.data.bankAssets) {
        setEarnAssets(response.data.bankAssets);
      }
    } catch (error) {
      console.error("Error fetching earn assets:", error);
    }
  };

  const fetchP2PFees = async () => {
    try {
      const response = await axios.get(
        "https://accounts.paybito.com/api/home/getP2PTxnFee"
      );
      setP2pFees(response.data.transactionFees);
    } catch (error) {
      console.error("Error fetching P2P fees:", error);
    }
  };



  const fetchICOFees = async () => {
    try {
      const response = await axios.get(
        "https://service.hashcashconsultants.com/hc_ico_platform/merchant/platform_fees"
      );
      setIcoFees(response.data.fees);
      setIcoPlans(response.data.plans);
    } catch (error) {
      console.error("Error fetching ICO fees:", error);
    }
  };

  const fetchNFTFees = async () => {
    try {
      const response = await axios.get(
        "https://accounts.paybito.com/api/home/getNFTFees"
      );
      setNftFees(response.data.nftFees);
    } catch (error) {
      console.error("Error fetching NFT fees:", error);
    }
  };

  const formatInvoiceAmount = (amount) => {
    if (!amount) return "";
    let parts = amount.replace("$", "").split("/");
    let numberPart = parseInt(parts[0]);
    if (isNaN(numberPart)) return amount;
    let formattedNumber = numberPart.toLocaleString("en-US");
    return "$" + formattedNumber + (parts[1] ? " / " + parts[1] : "");
  };

  return (
    <div>
      {/* Inner Banner */}
      <div className="inner-banner-exchange-fee">
        <div className="container">
          <div className="text-center">
            <h1 className="text-white">Limits and Fees</h1>
            <div className="banner-info-card">
              <ul className="ul-list">
                <li>
                  <CheckCircleIcon style={{ fontSize: '20px', marginRight: '12px', marginTop: '4px' }} />
                  <span>There are the fees charged to your end users.</span>
                </li>
                <li>
                  <CheckCircleIcon style={{ fontSize: '20px', marginRight: '12px', marginTop: '4px' }} />
                  <span>{localStorage.getItem("exchange")} returns these fees back to you in the form of commissions retaining a portion as transaction fees.</span>
                </li>
                <li>
                  <CheckCircleIcon style={{ fontSize: '20px', marginRight: '12px', marginTop: '4px' }} />
                  <span>The commission percentage returned to you from trading fees charged by {localStorage.getItem("exchange")} is based on the trading volume tier you are in.</span>
                </li>
                <li>
                  <CheckCircleIcon style={{ fontSize: '20px', marginRight: '12px', marginTop: '4px' }} />
                  <span>Your volume tier is based on the combined trading volume of all your users put together.</span>
                </li>
                {/* <li>
                  <CheckCircleIcon style={{ fontSize: '20px', marginRight: '12px', marginTop: '4px' }} />
                  <span>
                    Review your{" "}
                    <a href="https://launch-platform.paybito.com/my-commission" target="_blank" rel="noopener noreferrer">
                      tier and commission percentage.
                    </a>
                  </span>
                </li> */}
                <li>
                  <CheckCircleIcon style={{ fontSize: '20px', marginRight: '12px', marginTop: '4px' }} />
                  <span>The limits on this page apply to all end users.</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Main Fees Section */}
      <section className="fees-section">
        <div className="container">
          {/* Primary Tabs */}
          <div className="primary-tabs">
            <ul className="nav nav-tabs" role="tablist">
              <li className="nav-item">
                <a
                  className={`nav-link ${activeMainTab === "exchange" ? "active" : ""}`}
                  onClick={() => setActiveMainTab("exchange")}
                  role="tab"
                >
                  Exchange
                </a>
              </li>
              {brokerMarket.bank === 1 && (
                <li className="nav-item">
                  <a
                    className={`nav-link ${activeMainTab === "loans" ? "active" : ""}`}
                    onClick={() => setActiveMainTab("loans")}
                    role="tab"
                  >
                    Loans
                  </a>
                </li>
              )}
              {brokerMarket.bank === 1 && (
                <li className="nav-item">
                  <a
                    className={`nav-link ${activeMainTab === "earn" ? "active" : ""}`}
                    onClick={() => setActiveMainTab("earn")}
                    role="tab"
                  >
                    Earn
                  </a>
                </li>
              )}
              {brokerMarket.p2p === 1 && (
                <li className="nav-item">
                  <a
                    className={`nav-link ${activeMainTab === "p2p" ? "active" : ""}`}
                    onClick={() => setActiveMainTab("p2p")}
                    role="tab"
                  >
                    P2P
                  </a>
                </li>
              )}
              {brokerMarket.payments === 1 && (
                <li className="nav-item">
                  <a
                    className={`nav-link ${activeMainTab === "payments" ? "active" : ""}`}
                    onClick={() => setActiveMainTab("payments")}
                    role="tab"
                  >
                    Payments
                  </a>
                </li>
              )}
              {brokerMarket.ico === 1 && (
                <li className="nav-item">
                  <a
                    className={`nav-link ${activeMainTab === "ico" ? "active" : ""}`}
                    onClick={() => setActiveMainTab("ico")}
                    role="tab"
                  >
                    ICO
                  </a>
                </li>
              )}
              {brokerMarket.nft === 1 && (
                <li className="nav-item">
                  <a
                    className={`nav-link ${activeMainTab === "nft" ? "active" : ""}`}
                    onClick={() => setActiveMainTab("nft")}
                    role="tab"
                  >
                    NFT
                  </a>
                </li>
              )}
            </ul>
          </div>

          {/* Tab Content */}
          <div className="tab-content">
            {/* Exchange Tab */}
            {activeMainTab === "exchange" && (
              <div className="tab-pane fade show active">
                {/* Secondary Tabs for Exchange */}
                <div className="secondary-tabs">
                  <ul className="nav nav-tabs" role="tablist">
                    <li className="nav-item">
                      <a
                        className={`nav-link ${activeExchangeTab === "trading-fee" ? "active" : ""}`}
                        onClick={() => setActiveExchangeTab("trading-fee")}
                        role="tab"
                      >
                        Trading Fee
                      </a>
                    </li>
                    <li className="nav-item">
                      <a
                        className={`nav-link ${activeExchangeTab === "daily-limit" ? "active" : ""}`}
                        onClick={() => setActiveExchangeTab("daily-limit")}
                        role="tab"
                      >
                        Daily Limit
                      </a>
                    </li>
                    <li className="nav-item">
                      <a
                        className={`nav-link ${activeExchangeTab === "deposit-withdrawal" ? "active" : ""}`}
                        onClick={() => setActiveExchangeTab("deposit-withdrawal")}
                        role="tab"
                      >
                        Deposit & Withdrawal Fees
                      </a>
                    </li>
                    {isFutures === 1 && (
                      <li className="nav-item">
                        <a
                          className={`nav-link ${activeExchangeTab === "futures-lending" ? "active" : ""}`}
                          onClick={() => setActiveExchangeTab("futures-lending")}
                          role="tab"
                        >
                          Futures Lending Borrowing & Transaction Charge
                        </a>
                      </li>
                    )}
                    {isOptions === 1 && (
                      <li className="nav-item">
                        <a
                          className={`nav-link ${activeExchangeTab === "options-lending" ? "active" : ""}`}
                          onClick={() => setActiveExchangeTab("options-lending")}
                          role="tab"
                        >
                          Options Lending Borrowing & Transaction Charge
                        </a>
                      </li>
                    )}
                    <li className="nav-item">
                      <a
                        className={`nav-link ${activeExchangeTab === "collateral" ? "active" : ""}`}
                        onClick={() => setActiveExchangeTab("collateral")}
                        role="tab"
                      >
                        Collateral Deposit (Business Owners Only)
                      </a>
                    </li>
                  </ul>
                </div>

                {/* Exchange Sub Tab Content */}
                <div className="tab-content">
                  {/* Trading Fee */}
                  {activeExchangeTab === "trading-fee" && (
                    <div className="tab-pane fade show active">
                      <div className="table-wrapper fade-in">
                        <div className="table-responsive">
                          <table className="table fees-table text-center">
                            <thead>
                              <tr>
                                <th className="text-left">Volume Tier (Monthly Volume) (USD)</th>
                                <th>Maker Fee (%)</th>
                                <th>Taker Fee (%)</th>
                                <th>
                                  Maker Fee{" "}
                                  <span className="discount-badge">
                                    <LocalOfferIcon style={{ fontSize: '12px' }} /> {tradingCurrency}
                                  </span>{" "}
                                  (%)
                                </th>
                                <th>
                                  Taker Fee{" "}
                                  <span className="discount-badge">
                                    <LocalOfferIcon style={{ fontSize: '12px' }} /> {tradingCurrency}
                                  </span>{" "}
                                  (%)
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {loading ? (
                                <tr className="loading-row">
                                  <td colSpan="5">
                                    <div className="spinner-border" role="status">
                                      <span className="sr-only">Loading...</span>
                                    </div>
                                  </td>
                                </tr>
                              ) : (
                                tradingFees.map((fee, index) => (
                                  <tr key={index} className="table-row-navy">
                                    <td className="text-left" data-label="Volume Tier">
                                      {fee.volumeFrom} - {fee.volumeTo}
                                    </td>
                                    <td data-label="Maker Fee">
                                      {fee.makerFee.toString().length === 3 && fee.makerFee !== Math.floor(fee.makerFee)
                                        ? `${fee.makerFee}0`
                                        : fee.makerFee}
                                    </td>
                                    <td data-label="Taker Fee">
                                      {fee.takerFee.toString().length === 3 && fee.takerFee !== Math.floor(fee.takerFee)
                                        ? `${fee.takerFee}0`
                                        : fee.takerFee}
                                    </td>
                                    <td data-label={`Maker Fee ${tradingCurrency}`}>{fee.discountMakerFee}</td>
                                    <td data-label={`Taker Fee ${tradingCurrency}`}>{fee.discountTakerFee}</td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Daily Limit */}
                  {activeExchangeTab === "daily-limit" && (
                    <div className="tab-pane fade show active">
                      <div className="table-wrapper fade-in">
                        <div className="table-responsive">
                          <table className="table fees-table text-center">
                            <thead>
                              <tr>
                                <th>KYC Tier Type</th>
                                <th>Currency</th>
                                <th>Min Limit</th>
                                <th>Daily Buy Limit</th>
                                <th>Daily Sell Limit</th>
                                <th>Daily Send Limit</th>
                              </tr>
                            </thead>
                            <tbody>
                              {loading ? (
                                <tr className="loading-row table-row-navy">
                                  <td colSpan="6">
                                    <div className="spinner-border" role="status">
                                      <span className="sr-only">Loading...</span>
                                    </div>
                                  </td>
                                </tr>
                              ) : (
                                dailyLimits.map((limit, index) => (
                                  <tr key={index} className="table-row-navy">
                                    <td data-label="KYC Tier Type">{limit.tierType}</td>
                                    <td data-label="Currency">{limit.currency}</td>
                                    <td data-label="Min Limit">{limit.minLimit}</td>
                                    <td data-label="Daily Buy Limit">{limit.dailyBuyLimit}</td>
                                    <td data-label="Daily Sell Limit">{limit.dailySellLimit}</td>
                                    <td data-label="Daily Send Limit">{limit.dailySendLimit}</td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Deposit & Withdrawal */}
                  {activeExchangeTab === "deposit-withdrawal" && (
                    <div className="tab-pane fade show active">
                      <div className="table-wrapper fade-in">
                        <div className="table-responsive">
                          <table className="table fees-table text-center">
                            <thead>
                              <tr>
                                <th>Currency</th>
                                <th>Minimum Withdrawal</th>
                                <th>Maximum Withdrawal</th>
                                <th>Deposit</th>
                                <th>Withdrawal Fee</th>
                              </tr>
                            </thead>
                            <tbody>
                              {loading ? (
                                <tr className="loading-row table-row-navy">
                                  <td colSpan="5">
                                    <div className="spinner-border" role="status">
                                      <span className="sr-only">Loading...</span>
                                    </div>
                                  </td>
                                </tr>
                              ) : (
                                <>
                                  {depositWithdrawalFees.map((fee, index) => (
                                    <tr key={index} className="table-row-navy">
                                      <td data-label="Currency">{fee.currency}</td>
                                      <td data-label="Minimum Withdrawal">{fee.fromFee}</td>
                                      <td data-label="Maximum Withdrawal">{fee.toFee}</td>
                                      <td data-label="Deposit">Free</td>
                                      <td data-label="Withdrawal Fee">
                                        {fee.feeRate}% + {fee.minFee}
                                      </td>
                                    </tr>
                                  ))}
                                  {fiatLimits.map((limit, index) => (
                                    <tr key={`fiat-${index}`} className="table-row-navy">
                                      <td data-label="Currency">{limit.currency}</td>
                                      <td data-label="Minimum Withdrawal">{limit.minLimit}</td>
                                      <td data-label="Maximum Withdrawal">{limit.dailySendLimit}</td>
                                      <td data-label="Deposit">Free</td>
                                      <td data-label="Withdrawal Fee">{limit.txnCharge}%</td>
                                    </tr>
                                  ))}
                                </>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Futures Lending */}
                  {activeExchangeTab === "futures-lending" && isFutures === 1 && (
                    <div className="tab-pane fade show active">
                      <div className="table-wrapper fade-in">
                        <div className="table-responsive">
                          <table className="table fees-table text-center">
                            <thead>
                              <tr>
                                <th>Currency Name</th>
                                <th>Transfer In Margin Wallet</th>
                                <th>Borrowable</th>
                                <th>Transaction Charges (%)</th>
                                <th>Daily Interest / Yearly Interest Paid by Borrower</th>
                                <th>Daily Interest / Yearly Interest Earned by Lender</th>
                              </tr>
                            </thead>
                            <tbody>
                              {loading ? (
                                <tr className="loading-row">
                                  <td colSpan="6">
                                    <div className="spinner-border" role="status">
                                      <span className="sr-only">Loading...</span>
                                    </div>
                                  </td>
                                </tr>
                              ) : (
                                futuresData.map((item, index) => (
                                  <tr key={index} className="table-row-navy">
                                    <td data-label="Currency Name">{item.CURRENCY_NAME}</td>
                                    <td data-label="Transfer In Margin Wallet">
                                      <CheckCircleIcon style={{ color: '#2ecc71', fontSize: '20px' }} />
                                    </td>
                                    <td data-label="Borrowable">
                                      <CheckCircleIcon style={{ color: '#2ecc71', fontSize: '20px' }} />
                                    </td>
                                    <td data-label="Transaction Charges">{item.TXN_CHARGE}</td>
                                    <td data-label="Daily / Yearly Interest (Borrower)">
                                      {item.INTEREST}% / {item.INTEREST * 365}%
                                    </td>
                                    <td data-label="Daily / Yearly Interest (Lender)">
                                      {item.LENDER_EARNED_INTEREST * item.INTEREST}% /{" "}
                                      {item.LENDER_EARNED_INTEREST * item.INTEREST * 365}%
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Options Lending */}
                  {activeExchangeTab === "options-lending" && isOptions === 1 && (
                    <div className="tab-pane fade show active">
                      <div className="table-wrapper fade-in">
                        <div className="table-responsive">
                          <table className="table fees-table text-center">
                            <thead>
                              <tr>
                                <th>Currency Name</th>
                                <th>Transfer In Margin Wallet</th>
                                <th>Borrowable</th>
                                <th>Transaction Charges (%)</th>
                                <th>Daily Interest / Yearly Interest Paid by Borrower</th>
                                <th>Daily Interest / Yearly Interest Earned by Lender</th>
                              </tr>
                            </thead>
                            <tbody>
                              {loading ? (
                                <tr className="loading-row table-row-navy">
                                  <td colSpan="6">
                                    <div className="spinner-border" role="status">
                                      <span className="sr-only">Loading...</span>
                                    </div>
                                  </td>
                                </tr>
                              ) : (
                                optionsData.map((item, index) => (
                                  <tr key={index} className="table-row-navy">
                                    <td data-label="Currency Name">{item.CURRENCY_NAME}</td>
                                    <td data-label="Transfer In Margin Wallet">
                                      <CheckCircleIcon style={{ color: '#2ecc71', fontSize: '20px' }} />
                                    </td>
                                    <td data-label="Borrowable">
                                      <CheckCircleIcon style={{ color: '#2ecc71', fontSize: '20px' }} />
                                    </td>
                                    <td data-label="Transaction Charges">{item.TXN_CHARGE}</td>
                                    <td data-label="Daily / Yearly Interest (Borrower)">
                                      {item.INTEREST}% / {item.INTEREST * 365}%
                                    </td>
                                    <td data-label="Daily / Yearly Interest (Lender)">
                                      {item.LENDER_EARNED_INTEREST * item.INTEREST}% /{" "}
                                      {item.LENDER_EARNED_INTEREST * item.INTEREST * 365}%
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Collateral Deposit */}
                  {activeExchangeTab === "collateral" && (
                    <div className="tab-pane fade show active">
                      <div className="table-wrapper fade-in">
                        <div className="table-responsive">
                          <table className="table fees-table text-center">
                            <thead>
                              <tr>
                                <th>Monthly Trading Volume (From)</th>
                                <th>Monthly Trading Volume (To)</th>
                                <th>Collateral Deposit Requirement</th>
                              </tr>
                            </thead>
                            <tbody>
                              {loading ? (
                                <tr className="loading-row table-row-navy">
                                  <td colSpan="3">
                                    <div className="spinner-border" role="status">
                                      <span className="sr-only">Loading...</span>
                                    </div>
                                  </td>
                                </tr>
                              ) : (
                                collateralDeposit.map((item, index) => (
                                  <tr key={index} className="table-row-navy">
                                    <td data-label="Volume From">{item.volumeFrom}</td>
                                    <td data-label="Volume To">{item.volumeTo}</td>
                                    <td data-label="Deposit Requirement">{item.depositRequirement}</td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <p className="notice-text">
                  <strong>Please note that fees may be updated at any time.</strong>
                </p>
              </div>
            )}

            {/* Loans Tab */}
            {activeMainTab === "loans" && brokerMarket.bank === 1 && (
              <div className="tab-pane fade show active">
                <div className="secondary-tabs">
                  <ul className="nav nav-tabs" role="tablist">
                    <li className="nav-item">
                      <a className="nav-link active" role="tab">
                        Loanable Assets and Interest Rates charged by Loan Term
                      </a>
                    </li>
                  </ul>
                </div>
                <div className="tab-content">
                  <div className="tab-pane fade show active">
                    <div className="table-wrapper fade-in">
                      <div className="table-responsive">
                        <table className="table fees-table text-center">
                          <thead>
                            <tr>
                              <th rowSpan="2" style={{ verticalAlign: "middle" }}>
                                SL No.
                              </th>
                              <th rowSpan="2" style={{ verticalAlign: "middle" }}>
                                Loanable Assets
                              </th>
                              <th colSpan="2">Flexible Rates</th>
                              <th colSpan="2">7 Days - Stable Rate</th>
                              <th colSpan="2">30 Days - Stable Rate</th>
                            </tr>
                            <tr>
                              <th>Minute Rate</th>
                              <th>Annualized Rate (%)</th>
                              <th>Minute Rate</th>
                              <th>Annualized Rate (%)</th>
                              <th>Minute Rate</th>
                              <th>Annualized Rate (%)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {loading ? (
                              <tr className="loading-row table-row-navy">
                                <td colSpan="8">
                                  <div className="spinner-border" role="status">
                                    <span className="sr-only">Loading...</span>
                                  </div>
                                </td>
                              </tr>
                            ) : (
                              Object.entries(loanableAssets).map(([asset, durations], index) => (
                                <tr key={index} className="table-row-navy">
                                  <td data-label="SL No.">{index + 1}</td>
                                  <td data-label="Loanable Assets">{asset}</td>
                                  <td data-label="Flexible - Minute Rate">{durations[1]?.interestRatePerMinute || "-"}</td>
                                  <td data-label="Flexible - Annualized Rate">{durations[1]?.annualizedInterestRate || "-"}</td>
                                  <td data-label="7 Days - Minute Rate">{durations[2]?.interestRatePerMinute || "-"}</td>
                                  <td data-label="7 Days - Annualized Rate">{durations[2]?.annualizedInterestRate || "-"}</td>
                                  <td data-label="30 Days - Minute Rate">{durations[3]?.interestRatePerMinute || "-"}</td>
                                  <td data-label="30 Days - Annualized Rate">{durations[3]?.annualizedInterestRate || "-"}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Earn Tab */}
            {activeMainTab === "earn" && brokerMarket.bank === 1 && (
              <div className="tab-pane fade show active">
                <div className="secondary-tabs">
                  <ul className="nav nav-tabs" role="tablist">
                    <li className="nav-item">
                      <a className="nav-link active" role="tab">
                        Assets qualified for yield based on lock-in period
                      </a>
                    </li>
                  </ul>
                </div>
                <div className="tab-content">
                  <div className="tab-pane fade show active">
                    <div className="table-wrapper fade-in">
                      <div className="table-responsive">
                        <table className="table fees-table text-center">
                          <thead>
                            <tr>
                              <th>SL No.</th>
                              <th>Eligible Assets</th>
                              <th>Duration</th>
                              <th>Interest Rate (%)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {loading ? (
                              <tr className="loading-row table-row-navy">
                                <td colSpan="4">
                                  <div className="spinner-border" role="status">
                                    <span className="sr-only">Loading...</span>
                                  </div>
                                </td>
                              </tr>
                            ) : (
                              earnAssets.map((asset, index) => (
                                <tr key={index} className="table-row-navy">
                                  <td data-label="SL No.">{index + 1}</td>
                                  <td data-label="Asset">{asset.currency}</td>
                                  <td data-label="Duration">{asset.duration}</td>
                                  <td data-label="Interest Rate (%)">{asset.interestRate}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* P2P Tab */}
            {activeMainTab === "p2p" && brokerMarket.p2p === 1 && (
              <div className="tab-pane fade show active">
                <div className="secondary-tabs">
                  <ul className="nav nav-tabs" role="tablist">
                    <li className="nav-item">
                      <a className="nav-link active" role="tab">
                        P2P Transaction Fees
                      </a>
                    </li>
                  </ul>
                </div>
                <div className="tab-content">
                  <div className="tab-pane fade show active">
                    <div className="table-wrapper fade-in">
                      <div className="table-responsive">
                        <table className="table fees-table text-center">
                          <thead>
                            <tr>
                              <th>Asset</th>
                              <th>Transaction Fees</th>
                            </tr>
                          </thead>
                          <tbody>
                            {loading ? (
                              <tr className="loading-row table-row-navy">
                                <td colSpan="2">
                                  <div className="spinner-border" role="status">
                                    <span className="sr-only">Loading...</span>
                                  </div>
                                </td>
                              </tr>
                            ) : (
                              p2pFees.map((fee, index) => (
                                <tr key={index} className="table-row-navy">
                                  <td data-label="Asset">{fee.asset}</td>
                                  <td data-label="Transaction Fees">{fee.interestRate}%</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Payments Tab */}
            {activeMainTab === "payments" && brokerMarket.payments === 1 && (
              <div className="tab-pane fade show active">
                <div className="secondary-tabs">
                  <ul className="nav nav-tabs" role="tablist">
                    <li className="nav-item">
                      <a
                        className={`nav-link ${activePaymentsTab === "txn-fees" ? "active" : ""}`}
                        onClick={() => setActivePaymentsTab("txn-fees")}
                        role="tab"
                      >
                        Transaction Fees
                      </a>
                    </li>
                    <li className="nav-item">
                      <a
                        className={`nav-link ${activePaymentsTab === "plan-limit" ? "active" : ""}`}
                        onClick={() => setActivePaymentsTab("plan-limit")}
                        role="tab"
                      >
                        Plan Limit
                      </a>
                    </li>
                  </ul>
                </div>
                <div className="tab-content">
                  {activePaymentsTab === "txn-fees" && (
                    <div className="tab-pane fade show active">
                      <div className="table-wrapper fade-in">
                        <div className="table-responsive">
                          <table className="table fees-table text-center">
                            <thead>
                              <tr>
                                <th>SL No.</th>
                                <th>Currency</th>
                                <th>Txn. Charges</th>
                              </tr>
                            </thead>
                            <tbody>
                              {loading ? (
                                <tr className="loading-row table-row-navy">
                                  <td colSpan="3">
                                    <div className="spinner-border" role="status">
                                      <span className="sr-only">Loading...</span>
                                    </div>
                                  </td>
                                </tr>
                              ) : (
                                paymentsTxnFees.map((fee, index) => (
                                  <tr key={index} className="table-row-navy">
                                    <td data-label="SL No.">{index + 1}</td>
                                    <td data-label="Currency">{fee.currencyCode}</td>  {/* was fee.currency */}
                                    <td data-label="Txn. Charges">{fee.charge}%</td>  {/* was fee.txn_fees */}
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                  {activePaymentsTab === "plan-limit" && (
                    <div className="tab-pane fade show active">
                      <div className="table-wrapper fade-in">
                        <div className="table-responsive">
                          <table className="table fees-table text-center">
                            <thead>
                              <tr>
                                <th>SL No.</th>
                                <th>Plan</th>
                                <th>No. of Invoices</th>
                                <th>Total Invoice Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {loading ? (
                                <tr className="loading-row table-row-navy">
                                  <td colSpan="4">
                                    <div className="spinner-border" role="status">
                                      <span className="sr-only">Loading...</span>
                                    </div>
                                  </td>
                                </tr>
                              ) : (
                                <>
                                  {paymentsPlans.map((plan, index) => (
                                    <tr key={index} className="table-row-navy">
                                      <td data-label="SL No.">{index + 1}</td>
                                      <td data-label="Plan">{plan.plan}</td>
                                      <td data-label="No. of Invoices">{plan.no_of_invoices}</td>
                                      <td data-label="Total Invoice Amount">{formatInvoiceAmount(plan.invoice_amount)}</td>
                                    </tr>
                                  ))}
                                  <tr className="table-row-navy">
                                    <td data-label="SL No.">4</td>
                                    <td data-label="Plan">ENTERPRISE</td>
                                    <td colSpan="2" data-label="Info">
                                      Contact us for more info
                                    </td>
                                  </tr>
                                </>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <p className="notice-text">
                  <strong>
                    * Transaction fees can be updated by Business Owners from their{" "}
                    <a href="https://launch-platform.paybito.com/settings" target="_blank" rel="noopener noreferrer">
                      Launch Platform settings.
                    </a>
                  </strong>
                </p>
              </div>
            )}

            {/* ICO Tab */}
            {activeMainTab === "ico" && brokerMarket.ico === 1 && (
              <div className="tab-pane fade show active">
                <div className="secondary-tabs">
                  <ul className="nav nav-tabs" role="tablist">
                    <li className="nav-item">
                      <a
                        className={`nav-link ${activeICOTab === "txn-fees" ? "active" : ""}`}
                        onClick={() => setActiveICOTab("txn-fees")}
                        role="tab"
                      >
                        Transaction Fees
                      </a>
                    </li>
                    <li className="nav-item">
                      <a
                        className={`nav-link ${activeICOTab === "plan-limit" ? "active" : ""}`}
                        onClick={() => setActiveICOTab("plan-limit")}
                        role="tab"
                      >
                        Plan Limit
                      </a>
                    </li>
                  </ul>
                </div>
                <div className="tab-content">
                  {activeICOTab === "txn-fees" && (
                    <div className="tab-pane fade show active">
                      <div className="table-wrapper fade-in">
                        <div className="table-responsive">
                          <table className="table fees-table text-center">
                            <thead>
                              <tr>
                                <th>SL No.</th>
                                <th>Currency</th>
                                <th>Txn. Charges</th>
                              </tr>
                            </thead>
                            <tbody>
                              {loading ? (
                                <tr className="loading-row table-row-navy">
                                  <td colSpan="3">
                                    <div className="spinner-border" role="status">
                                      <span className="sr-only">Loading...</span>
                                    </div>
                                  </td>
                                </tr>
                              ) : (
                                icoFees.map((fee, index) => (
                                  <tr key={index} className="table-row-navy">
                                    <td data-label="SL No.">{index + 1}</td>
                                    <td data-label="Currency">{fee.currency}</td>
                                    <td data-label="Txn. Charges">{fee.txn_fees}%</td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                  {activeICOTab === "plan-limit" && (
                    <div className="tab-pane fade show active">
                      <div className="table-wrapper fade-in">
                        <div className="table-responsive">
                          <table className="table fees-table text-center">
                            <thead>
                              <tr>
                                <th>SL No.</th>
                                <th>Plan</th>
                                <th>No. of Investments</th>
                                <th>Total Investment Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {loading ? (
                                <tr className="loading-row table-row-navy">
                                  <td colSpan="4">
                                    <div className="spinner-border" role="status">
                                      <span className="sr-only">Loading...</span>
                                    </div>
                                  </td>
                                </tr>
                              ) : (
                                <>
                                  {icoPlans.map((plan, index) => (
                                    <tr key={index} className="table-row-navy">
                                      <td data-label="SL No.">{index + 1}</td>
                                      <td data-label="Plan">{plan.plan}</td>
                                      <td data-label="No. of Investments">{plan.no_of_invoices}</td>
                                      <td data-label="Total Investment Amount">{formatInvoiceAmount(plan.invoice_amount)}</td>
                                    </tr>
                                  ))}
                                  <tr className="table-row-navy">
                                    <td data-label="SL No.">4</td>
                                    <td data-label="Plan">ENTERPRISE</td>
                                    <td colSpan="2" data-label="Info">
                                      Contact us for more info
                                    </td>
                                  </tr>
                                </>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <p className="notice-text">
                  <strong>
                    * Transaction fees can be updated by Business Owners from their{" "}
                    <a href="https://launch-platform.paybito.com/settings" target="_blank" rel="noopener noreferrer">
                      Launch Platform settings.
                    </a>
                  </strong>
                </p>
              </div>
            )}

            {/* NFT Tab */}
            {activeMainTab === "nft" && brokerMarket.nft === 1 && (
              <div className="tab-pane fade show active">
                <div className="table-wrapper fade-in">
                  <div className="table-responsive">
                    <table className="table fees-table text-center">
                      <thead>
                        <tr>
                          <th>SL No.</th>
                          <th>NFT Operation</th>
                          <th>ETH Balance</th>
                          <th>ETH Network Fee</th>
                          <th>Transaction Charge</th>
                          <th>Comment</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr className="loading-row table-row-navy">
                            <td colSpan="6">
                              <div className="spinner-border" role="status">
                                <span className="sr-only">Loading...</span>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          nftFees.map((fee, index) => (
                            <tr key={index} className="table-row-navy">
                              <td data-label="SL No.">{index + 1}</td>
                              <td data-label="NFT Operation">{fee.Operation}</td>
                              <td data-label="ETH Balance">{fee.ETH_Balance}</td>
                              <td data-label="ETH Network Fee">{fee.ETH_NETWORK_FEE}</td>
                              <td data-label="Transaction Charge">{fee.TransactionCharge}</td>
                              <td data-label="Comment">{fee.Comment}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ExchangeFees;
